/**
 * Client for cloud posture checks (bank row II-17). Wire shapes mirror
 * apps/api-controller/src/modules/discovery/cloud-posture/repository.ts.
 * Kept out of api.ts so parallel work on that file does not collide with this.
 */

import { ApiError, apiFetch } from './api';

export type PostureStatus = 'violation' | 'compliant' | 'not_checked';

export interface PostureCheck {
  id: string;
  provider: string;
  region: string | null;
  check: string;
  resourceType: string;
  resourceRef: string;
  resourceName: string;
  status: PostureStatus;
  severity: 'critical' | 'high' | 'medium' | 'low' | null;
  setting: unknown;
  reason: string | null;
  outcome: string | null;
  /** The first linked assets (at most 20); `assetCount` is all of them in view. */
  assets: Array<{ id: string; name: string; ref: string }>;
  assetCount?: number;
  findingIds: string[];
  firstSeenAt: string;
  lastCheckedAt: string;
}

export type PostureScope = 'all' | 'linked';

/** `aws`, or the database engine a result is about (Release 8: database exposure checks). */
export type PostureProvider = 'aws' | 'mysql' | 'mssql' | 'oracle';
export const POSTURE_PROVIDERS: readonly PostureProvider[] = ['aws', 'mysql', 'mssql', 'oracle'];
export const PROVIDER_LABELS: Record<PostureProvider, string> = {
  aws: 'AWS',
  mysql: 'MySQL',
  mssql: 'SQL Server',
  oracle: 'Oracle',
};
export function isDatabaseProvider(p: string): boolean {
  return p === 'mysql' || p === 'mssql' || p === 'oracle';
}

export interface PostureList {
  /** `linked`: only results linked to assets of the caller's units. */
  scope?: PostureScope;
  checks: PostureCheck[];
  /** Every result matching the request; `checks` stops at the list limit. */
  total?: number;
  counts: Partial<Record<PostureStatus, number>>;
  /** Audit events of posture ingests not yet written to the audit chain. */
  auditPending?: number;
  /** When the newest result in this scope arrived (null: none yet). */
  lastRunAt?: string | null;
  /** The controller's clock when it answered; staleness is measured on it. */
  serverTime?: string;
}

/** The problem type the controller answers a unit-limited session with. */
export const POSTURE_NEEDS_ALL_UNITS = 'https://foctta.com/problems/cloud_posture_needs_all_units';

export async function listCloudPostureChecks(
  opts: { status?: PostureStatus; scope?: PostureScope; provider?: PostureProvider } = {},
): Promise<PostureList> {
  const params = new URLSearchParams();
  if (opts.status) params.set('status', opts.status);
  if (opts.provider) params.set('provider', opts.provider);
  if (opts.scope && opts.scope !== 'all') params.set('scope', opts.scope);
  const q = params.toString();
  return apiFetch<PostureList>(`/v1/discovery/cloud-posture/checks${q ? `?${q}` : ''}`);
}

/**
 * The full list, or, for a session limited to some business units (refused
 * the account-wide list with 403 `cloud_posture_needs_all_units`), the results
 * linked to its units' assets. Any other error is thrown.
 */
export async function loadCloudPosture(provider?: PostureProvider): Promise<PostureList> {
  const only = provider ? { provider } : {};
  try {
    return await listCloudPostureChecks(only);
  } catch (e: unknown) {
    if (e instanceof ApiError && e.status === 403 && (e.body as { type?: unknown } | null)?.type === POSTURE_NEEDS_ALL_UNITS) {
      return { ...(await listCloudPostureChecks({ ...only, scope: 'linked' })), scope: 'linked' };
    }
    throw e;
  }
}
